import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { Layout } from "@/components/Layout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertCircle, ArrowLeft } from "lucide-react";
import { getOrderDetailQuery, OrderDetailData } from "@/types/orders";
import { getCustomerDisplayName } from "@/lib/formatters";
import { supabase } from "@/integrations/supabase/client";
import { queryKeys } from "@/lib/query-keys";
// Detail components
import { OrderHeader } from "@/components/orders/detail/OrderHeader";
import { OrderBasicInfoCard } from "@/components/orders/detail/OrderBasicInfoCard";
import { OrderLinesCard } from "@/components/orders/detail/OrderLinesCard";
import { OrderCustomerCard } from "@/components/orders/detail/OrderCustomerCard";
import { OrderCemeteryCard } from "@/components/orders/detail/OrderCemeteryCard";
import { OrderPartnerCard } from "@/components/orders/detail/OrderPartnerCard";
import { OrderWorkDetailsCard } from "@/components/orders/detail/OrderWorkDetailsCard";
import { OrderNotesCard } from "@/components/orders/detail/OrderNotesCard";
import { OrderMaterialsTab } from "@/components/orders/detail/OrderMaterialsTab";
import { OrderInstallationsCard } from "@/components/orders/detail/OrderInstallationsCard";
import { OrderDetailSkeleton } from "@/components/orders/detail/OrderDetailSkeleton";
import { OrderAssetsCard } from "@/components/orders/detail/OrderAssetsCard";
import { OrderCemeteryApprovalCard } from "@/components/orders/detail/OrderCemeteryApprovalCard";
import { OrderCommunicationCard } from "@/components/orders/detail/OrderCommunicationCard";
import type { CemeteryApprovalStatus } from "@/config/order-constants";

export default function OrderDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    data: order,
    isLoading,
    error,
  } = useQuery({
    queryKey: queryKeys.orders.detail(id!),
    queryFn: async () => {
      const { data, error } = await getOrderDetailQuery(id!);
      if (error) throw error;
      return data as OrderDetailData;
    },
    enabled: !!id,
  });

  // Subscribe to realtime updates for this order
  useEffect(() => {
    if (!id) return;

    const channel = supabase
      .channel(`order-${id}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "orders",
          filter: `id=eq.${id}`,
        },
        (payload) => {
          console.log("Order updated via realtime:", payload);
          // Invalidate the query to refetch the latest data
          queryClient.invalidateQueries({ queryKey: queryKeys.orders.detail(id!) });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [id, queryClient]);

  // Use centralized getCustomerDisplayName from formatters

  if (isLoading) {
    return (
      <Layout>
        <OrderDetailSkeleton />
      </Layout>
    );
  }

  if (error || !order) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center py-12 space-y-4">
          <AlertCircle className="h-12 w-12 text-destructive" />
          <h2 className="text-xl font-semibold">Tilausta ei löytynyt</h2>
          <p className="text-muted-foreground">
            Tilaus on poistettu tai sinulla ei ole oikeuksia nähdä sitä.
          </p>
          <Button onClick={() => navigate("/orders")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Takaisin tilauksiin
          </Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <OrderHeader order={order} />

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column (2/3) */}
          <div className="lg:col-span-2 space-y-6">
            <OrderBasicInfoCard order={order} />
            <OrderLinesCard order={order} />
            <OrderAssetsCard
              orderProofs={order.order_proofs}
              orderFiles={order.order_files}
              orderId={order.id}
              orderNumber={order.order_number}
              customerName={getCustomerDisplayName(order.customers)}
              customerEmail={order.customers?.email}
            />
            {order.order_type === "pickup_work" && (
              <OrderWorkDetailsCard order={order} />
            )}
            <OrderInstallationsCard orderId={order.id} orderNumber={order.order_number} />
          </div>

          {/* Right column (1/3) */}
          <div className="space-y-6">
            <OrderCustomerCard
              customer={order.customers}
              orderId={order.id}
              orderNumber={order.order_number}
            />
            <OrderCemeteryCard cemetery={order.cemeteries} />
            <OrderCemeteryApprovalCard
              orderId={order.id}
              orderNumber={order.order_number}
              requiresApproval={order.requires_cemetery_approval ?? false}
              approvalStatus={(order.cemetery_approval_status as CemeteryApprovalStatus) ?? "not_required"}
              approvalRequestedAt={order.cemetery_approval_requested_at ?? null}
              approvalRespondedAt={order.cemetery_approval_responded_at ?? null}
              approvalNotes={order.cemetery_approval_notes ?? null}
              approvalMessage={order.cemetery_approval_message ?? null}
              approvalProofId={order.cemetery_approval_proof_id ?? null}
              cemetery={order.cemeteries ? {
                id: order.cemeteries.id,
                name: order.cemeteries.name,
                contact_email: order.cemeteries.contact_email,
                contact_name: order.cemeteries.contact_name,
              } : null}
            />
            {order.partners && (
              <OrderPartnerCard partner={order.partners} order={order} />
            )}
            <OrderNotesCard notes={order.notes} orderId={order.id} />
            <OrderCommunicationCard
              orderId={order.id}
              customerId={order.customers?.id}
            />
          </div>
        </div>

        {/* Materials section */}
        <OrderMaterialsTab orderNumber={order.order_number} />
      </div>
    </Layout>
  );
}
